import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Save, 
  Building, 
  FileText, 
  Mail, 
  Printer, 
  DollarSign,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Palette,
  Type,
  Layout,
  Calendar,
  MapPin,
  Phone,
  Globe,
  User,
  Edit3,
  Lock,
  Shield,
  Info,
  Square
} from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================

const ToggleCard = ({ label, checked, onChange, description, disabled = false }) => (
  <div className={`flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700 transition-colors ${
    disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800/70'
  }`}>
    <div className="flex-1">
      <label className={`font-medium text-gray-200 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        {label}
      </label>
      {description && (
        <p className={`text-sm mt-1 ${disabled ? 'text-gray-500' : 'text-gray-400'}`}>
          {description}
        </p>
      )}
    </div>
    <label className={`relative inline-flex items-center ml-4 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={(e) => !disabled && onChange(e.target.checked)}
        disabled={disabled}
      />
      <div className={`w-11 h-6 bg-gray-700 ${disabled ? '' : 'peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300/30'} rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
        disabled ? 'peer-checked:bg-gray-600' : 'peer-checked:bg-blue-600'
      }`}></div>
    </label>
  </div>
);

const InputField = ({ label, value, onChange, icon, placeholder, type = 'text' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">
      {label}
    </label>
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          {icon}
        </div>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full ${icon ? 'pl-10' : 'pl-4'} pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
      />
    </div>
  </div>
);

const TextareaField = ({ label, value, onChange, icon, rows = 3, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">
      {label}
    </label>
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-3 text-gray-400">
          {icon}
        </div>
      )}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className={`w-full ${icon ? 'pl-10' : 'pl-4'} pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
      />
    </div>
  </div>
);

const SelectField = ({ label, value, onChange, options, icon }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">
      {label}
    </label>
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10">
          {icon}
        </div>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full ${icon ? 'pl-10' : 'pl-4'} pr-10 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none`}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
        ▼
      </div>
    </div>
  </div>
);

// ============================================================================
// BORDER CUSTOMIZATION CARD
// ============================================================================

const BorderCard = ({ label, section, settings, onChange, icon, description }) => (
  <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4 hover:bg-gray-800/70 transition-colors">
    <div className="flex items-center gap-3 mb-4">
      {icon}
      <div className="flex-1">
        <h4 className="font-medium text-gray-200">{label}</h4>
        {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
      </div>
    </div>
    
    <div className="grid grid-cols-3 gap-3">
      {/* Border Color */}
      <div>
        <label className="block text-xs font-medium text-gray-300 mb-1">Color</label>
        <input
          type="color"
          value={settings.color}
          onChange={(e) => onChange(section, 'color', e.target.value)}
          className="w-full h-10 rounded border border-gray-600 bg-gray-700 cursor-pointer"
        />
      </div>
      
      {/* Border Width */}
      <div>
        <label className="block text-xs font-medium text-gray-300 mb-1">Width</label>
        <input
          type="text"
          value={settings.width}
          onChange={(e) => onChange(section, 'width', e.target.value)}
          placeholder="2px"
          className="w-full px-2 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">e.g., 2px, 0.5rem</p>
      </div>
      
      {/* Border Style */}
      <div>
        <label className="block text-xs font-medium text-gray-300 mb-1">Style</label>
        <select
          value={settings.style}
          onChange={(e) => onChange(section, 'style', e.target.value)}
          className="w-full px-2 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="solid">Solid</option>
          <option value="dashed">Dashed</option>
          <option value="dotted">Dotted</option>
          <option value="double">Double</option>
        </select>
      </div>
    </div>
    
    {/* Preview */}
    <div className="mt-3 p-3 rounded" style={{
      border: `${settings.width} ${settings.style} ${settings.color}`,
      backgroundColor: 'rgba(31, 41, 55, 0.5)'
    }}>
      <p className="text-xs text-gray-400 text-center">Preview</p>
    </div>
  </div>
);

/**
 * ============================================================================
 * INVOICE SETTINGS CONTROL PANEL
 * ============================================================================
 * 
 * Comprehensive settings management for invoice customization
 * 
 * Features:
 * - Display section toggles (17 toggles) - WITH Invoice Summary dependency enforcement
 * - Border customization for key sections
 * - Company information
 * - Template settings (colors, fonts, layout)
 * - Document settings
 * - Discount settings  
 * - Multi-tenant email settings
 * - Print settings
 * - Terms & conditions (with heading control)
 * - Footer settings
 * - Advanced settings
 */
const InvoiceSettingsControlPanel = ({ currentCompany, getCompanyContext }) => {
  
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  const [activeTab, setActiveTab] = useState('display');
  const [settings, setSettings] = useState({
    // Display Section Toggles - 17 TOGGLES TOTAL
    displaySections: {
      showCompanyLogo: true,
      showCustomerInfo: true,
      showInvoiceNumber: true,
      showInvoiceDate: true,
      showBikeDetails: true,
      showRentalPeriod: true,
      showPriceBreakdown: true,
      showAddons: true,
      showDiscount: true,
      showInvoiceSummary: true,  // PARENT toggle
      showTotalAmount: true,     // CHILD toggle (depends on Invoice Summary)
      showPaymentStatus: true,
      showDepositInfo: true,
      showFooter: true,
      showDocumentSection: true,
      showQRCode: false,
      showBarcode: false,
      showSignature: true
    },
    
    // Border Settings
    borderSettings: {
      billTo: {
        color: '#93c5fd',
        width: '2px',
        style: 'solid'
      },
      vehicleInfo: {
        color: '#fdba74',
        width: '2px',
        style: 'solid'
      },
      invoiceDetails: {
        color: '#cbd5e1',
        width: '2px',
        style: 'solid'
      },
      pickup: {
        color: '#86efac',
        width: '2px',
        style: 'solid'
      },
      return: {
        color: '#fca5a5',
        width: '2px',
        style: 'solid'
      }
    },
    
    // Company Information
    companyInfo: {
      companyName: '',
      companyEmail: '',
      companyPhone: '',
      companyWebsite: '',
      address: ''
    },
    
    // Template Settings
    templateSettings: {
      invoicePrefix: 'INV',
      dateFormat: 'MM/DD/YYYY',
      currencySymbol: '$',
      currencyPosition: 'before',
      primaryColor: '#2563eb',
      accentColor: '#10b981',
      fontSize: 'medium',
      fontFamily: 'Arial'
    },
    
    // Document Settings
    documentSettings: {
      autoAttachDocuments: true,
      requiredDocumentTypes: ['License', 'Identity'],
      documentRetentionDays: 90,
      showDocumentStatus: true,
      allowDocumentDownload: true,
      compressDocuments: false
    },
    
    // Discount Settings
    discountSettings: {
      showDiscountBadge: true,
      discountBadgeColor: '#10b981',
      showSavingsAmount: true,
      savingsTextColor: '#059669',
      discountLabelText: 'Discount Applied'
    },
    
    // Multi-Tenant Email Settings
    emailSettings: {
      autoSendEmail: false,
      emailProvider: 'gmail',
      senderEmail: '',
      senderPassword: '',
      smtpHost: '',
      smtpPort: 587,
      smtpSecure: false,
      subjectTemplate: 'Invoice {invoiceNumber} from {companyName}',
      invoiceViewUrl: '',
      ccAddresses: [],
      bccAddresses: []
    },
    
    // Print Settings
    printSettings: {
      showPrintButton: true,
      showDownloadButton: true,
      paperSize: 'A4',
      orientation: 'portrait',
      margins: 'normal'
    },
    
    // Terms & Conditions - SINGLE SOURCE OF TRUTH
    termsSettings: {
      showTerms: true,
      showTermsHeading: true,
      termsHeading: '',
      termsText: ''
    },
    
    // Footer Settings
    footerSettings: {
      showFooter: true,
      footerText: 'Thank you for your business!',
      showPageNumbers: false,
      showGeneratedBy: true
    },
    
    // Advanced Settings
    advancedSettings: {
      enableAuditLog: true,
      autoNumbering: true,
      invoiceNumberFormat: 'INV-YYYY-NNNN',
      timezone: 'UTC',
      language: 'en',
      enableNotifications: true,
      debugMode: false
    },
    
    // Border Settings
    borderSettings: {
      billTo: {
        enabled: true,
        color: '#93c5fd',
        width: '2px',
        style: 'solid'
      },
      vehicleInfo: {
        enabled: true,
        color: '#fdba74',
        width: '2px',
        style: 'solid'
      },
      invoiceDetails: {
        enabled: true,
        color: '#d1d5db',
        width: '2px',
        style: 'solid'
      },
      pickup: {
        enabled: true,
        color: '#86efac',
        width: '2px',
        style: 'solid'
      },
      return: {
        enabled: true,
        color: '#fca5a5',
        width: '2px',
        style: 'solid'
      }
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // ============================================================================
  // COMPANY CONTEXT
  // ============================================================================
  
  const companyId = getCompanyContext ? getCompanyContext() : currentCompany?.id;
  
  // ============================================================================
  // LOAD SETTINGS FROM FIREBASE
  // ============================================================================
  
  useEffect(() => {
    const loadSettings = async () => {
      if (!companyId) {
        console.log('No company context for settings');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const settingsRef = doc(db, `companies/${companyId}/settings`, 'invoiceSettings');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          console.log('✅ Loaded settings:', data);
          setSettings(prev => ({
            ...prev,
            ...data
          }));
        } else {
          console.log('No existing settings, using defaults');
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadSettings();
  }, [companyId]);
  
  // ============================================================================
  // SAVE SETTINGS TO FIREBASE
  // ============================================================================
  
  const handleSave = async () => {
    if (!companyId) {
      alert('No company context available');
      return;
    }
    
    try {
      setSaving(true);
      setSaveStatus(null);
      
      const settingsRef = doc(db, `companies/${companyId}/settings`, 'invoiceSettings');
      
      await setDoc(settingsRef, settings, { merge: true });
      
      console.log('✅ Settings saved successfully');
      setSaveStatus('success');
      
      setTimeout(() => setSaveStatus(null), 3000);
      
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('error');
      alert('Failed to save settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };
  
  // ============================================================================
  // HANDLE SETTING CHANGES WITH DEPENDENCY ENFORCEMENT
  // ============================================================================
  
  const handleChange = (category, field, value) => {
    setSettings(prev => {
      const updated = {
        ...prev,
        [category]: {
          ...(prev[category] || {}),
          [field]: value
        }
      };
      
      // ⭐ CRITICAL LOGIC: Auto-disable Total Amount when Invoice Summary is turned OFF
      if (category === 'displaySections' && field === 'showInvoiceSummary' && value === false) {
        console.log('🔒 Invoice Summary disabled → Auto-disabling Total Amount');
        updated.displaySections.showTotalAmount = false;
      }
      
      return updated;
    });
  };
  
  // ============================================================================
  // HANDLE BORDER SETTINGS CHANGES
  // ============================================================================
  
  const handleBorderChange = (section, property, value) => {
    setSettings(prev => ({
      ...prev,
      borderSettings: {
        ...prev.borderSettings,
        [section]: {
          ...prev.borderSettings[section],
          [property]: value
        }
      }
    }));
  };
  
  // ============================================================================
  // HANDLE TOTAL AMOUNT TOGGLE WITH VALIDATION
  // ============================================================================
  
  const handleTotalAmountToggle = (checked) => {
    // ⭐ VALIDATION: Don't allow enabling Total Amount if Invoice Summary is OFF
    if (checked && !settings.displaySections.showInvoiceSummary) {
      alert(
        '⚠️ Cannot enable "Show Total Amount"\n\n' +
        'Please enable "Show Invoice Summary" first.\n\n' +
        'The total amount is displayed inside the invoice summary box, ' +
        'so the summary must be visible to show the total.'
      );
      return;
    }
    
    handleChange('displaySections', 'showTotalAmount', checked);
  };
  
  // ============================================================================
  // TABS CONFIGURATION
  // ============================================================================
  
  const tabs = [
    { id: 'display', label: 'Display Sections', icon: Eye },
    { id: 'borders', label: 'Borders', icon: Square },
    { id: 'company', label: 'Company Info', icon: Building },
    { id: 'template', label: 'Template', icon: Palette },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'discount', label: 'Discounts', icon: DollarSign },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'print', label: 'Print', icon: Printer },
    { id: 'terms', label: 'Terms', icon: FileText },
    { id: 'footer', label: 'Footer', icon: Layout },
    { id: 'advanced', label: 'Advanced', icon: Settings }
  ];
  
  // ============================================================================
  // LOADING STATE
  // ============================================================================
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading settings...</div>
      </div>
    );
  }
  
  // ============================================================================
  // RENDER COMPONENT
  // ============================================================================
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* HEADER */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Settings className="text-blue-500" size={32} />
            Invoice Settings Control Panel
          </h1>
          <p className="text-gray-400">
            Customize how invoices are displayed and generated for your company
          </p>
        </div>
        
        {/* TABS NAVIGATION */}
        <div className="mb-6 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* MAIN CONTENT AREA */}
        <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 shadow-2xl border border-gray-800">
          
          {/* DISPLAY SECTIONS TAB */}
          {activeTab === 'display' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Eye className="text-green-500" />
                  Display Section Controls
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Toggle which sections appear on invoices (17 toggles available). Terms & Conditions managed in Terms tab.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Row 1 */}
                <ToggleCard
                  label="Show Company Logo"
                  checked={settings.displaySections.showCompanyLogo}
                  onChange={(checked) => handleChange('displaySections', 'showCompanyLogo', checked)}
                  description="Display company logo in invoice header"
                />
                
                <ToggleCard
                  label="Show Customer Info"
                  checked={settings.displaySections.showCustomerInfo}
                  onChange={(checked) => handleChange('displaySections', 'showCustomerInfo', checked)}
                  description="Display customer details (Bill To section)"
                />
                
                {/* Row 2 */}
                <ToggleCard
                  label="Show Invoice Number"
                  checked={settings.displaySections.showInvoiceNumber}
                  onChange={(checked) => handleChange('displaySections', 'showInvoiceNumber', checked)}
                  description="Display invoice number field"
                />
                
                <ToggleCard
                  label="Show Invoice Date"
                  checked={settings.displaySections.showInvoiceDate}
                  onChange={(checked) => handleChange('displaySections', 'showInvoiceDate', checked)}
                  description="Show invoice date and due date"
                />
                
                {/* Row 3 */}
                <ToggleCard
                  label="Show Bike Details"
                  checked={settings.displaySections.showBikeDetails}
                  onChange={(checked) => handleChange('displaySections', 'showBikeDetails', checked)}
                  description="Display vehicle information card"
                />
                
                <ToggleCard
                  label="Show Rental Period"
                  checked={settings.displaySections.showRentalPeriod}
                  onChange={(checked) => handleChange('displaySections', 'showRentalPeriod', checked)}
                  description="Show pickup and return dates"
                />
                
                {/* Row 4 */}
                <ToggleCard
                  label="Show Price Breakdown"
                  checked={settings.displaySections.showPriceBreakdown}
                  onChange={(checked) => handleChange('displaySections', 'showPriceBreakdown', checked)}
                  description="Display services table with itemized breakdown"
                />
                
                <ToggleCard
                  label="Show Add-ons"
                  checked={settings.displaySections.showAddons}
                  onChange={(checked) => handleChange('displaySections', 'showAddons', checked)}
                  description="Show add-on items in services table"
                />
                
                {/* Row 5 */}
                <ToggleCard
                  label="Show Discount"
                  checked={settings.displaySections.showDiscount}
                  onChange={(checked) => handleChange('displaySections', 'showDiscount', checked)}
                  description="Display discount information in summary"
                />
                
                <ToggleCard
                  label="Show Payment Status"
                  checked={settings.displaySections.showPaymentStatus}
                  onChange={(checked) => handleChange('displaySections', 'showPaymentStatus', checked)}
                  description="Display payment status (PAID/DUE)"
                />
                
                {/* Row 6 */}
                <ToggleCard
                  label="Show Deposit Info"
                  checked={settings.displaySections.showDepositInfo}
                  onChange={(checked) => handleChange('displaySections', 'showDepositInfo', checked)}
                  description="Show security deposit information and amount"
                />
                
                <ToggleCard
                  label="Show Footer"
                  checked={settings.displaySections.showFooter}
                  onChange={(checked) => handleChange('displaySections', 'showFooter', checked)}
                  description="Display invoice footer with metadata"
                />
                
                {/* Row 7 */}
                <ToggleCard
                  label="Show Document Section"
                  checked={settings.displaySections.showDocumentSection}
                  onChange={(checked) => handleChange('displaySections', 'showDocumentSection', checked)}
                  description="Display uploaded customer documents"
                />
                
                <ToggleCard
                  label="Show QR Code"
                  checked={settings.displaySections.showQRCode}
                  onChange={(checked) => handleChange('displaySections', 'showQRCode', checked)}
                  description="Display scannable QR code on invoice"
                />
                
                {/* Row 8 */}
                <ToggleCard
                  label="Show Barcode"
                  checked={settings.displaySections.showBarcode}
                  onChange={(checked) => handleChange('displaySections', 'showBarcode', checked)}
                  description="Display barcode with invoice number"
                />
                
                <ToggleCard
                  label="Show Signature"
                  checked={settings.displaySections.showSignature}
                  onChange={(checked) => handleChange('displaySections', 'showSignature', checked)}
                  description="Display customer signature section"
                />
              </div>
              
              {/* ⭐⭐⭐ INVOICE SUMMARY & TOTAL AMOUNT - WITH DEPENDENCY ENFORCEMENT ⭐⭐⭐ */}
              <div className="space-y-4 mt-8">
                <div className="border-t border-gray-700 pt-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Invoice Summary Settings</h4>
                  
                  {/* PARENT TOGGLE: Invoice Summary */}
                  <div className="mb-4">
                    <ToggleCard
                      label="Show Invoice Summary"
                      checked={settings.displaySections.showInvoiceSummary}
                      onChange={(checked) => handleChange('displaySections', 'showInvoiceSummary', checked)}
                      description="Display the complete invoice summary box with financial breakdown (rental, add-ons, subtotal, discount, total, payment status)"
                    />
                  </div>
                  
                  {/* CHILD TOGGLE: Total Amount (DEPENDS ON Invoice Summary) */}
                  <div className={`${!settings.displaySections.showInvoiceSummary ? 'ml-8 border-l-4 border-yellow-600/50 pl-4' : ''}`}>
                    <ToggleCard
                      label="Show Total Amount"
                      checked={settings.displaySections.showTotalAmount}
                      onChange={handleTotalAmountToggle}
                      disabled={!settings.displaySections.showInvoiceSummary}
                      description={
                        !settings.displaySections.showInvoiceSummary
                          ? '⚠️ DISABLED: Requires "Show Invoice Summary" to be enabled first'
                          : 'Show the total amount line inside the invoice summary box'
                      }
                    />
                  </div>
                  
                  {/* DEPENDENCY WARNING BANNER */}
                  {!settings.displaySections.showInvoiceSummary && (
                    <div className="mt-4 ml-8 p-4 bg-yellow-900/30 border border-yellow-600/50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle size={18} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-yellow-200">
                          <p className="font-semibold mb-1">Dependency Requirement</p>
                          <p>The total amount is displayed inside the invoice summary box. You must enable "Show Invoice Summary" before you can show the total amount.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* TOGGLE COUNT SUMMARY */}
              <div className="mt-6 p-4 bg-blue-900/30 border border-blue-600/50 rounded-lg">
                <p className="text-blue-200 text-sm font-medium mb-2">
                  ✅ All 17 display toggles available
                </p>
                <div className="text-blue-300 text-xs space-y-1">
                  <p>• 15 standard toggles + Invoice Summary + Total Amount = 17 total</p>
                  <p>• Total Amount toggle depends on Invoice Summary (enforced dependency)</p>
                </div>
              </div>
              
              {/* DEPENDENCY LOGIC EXPLANATION */}
              <div className="mt-4 p-4 bg-green-900/20 border border-green-600/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-green-200 mb-2">Smart Dependency Enforcement</h4>
                    <div className="text-sm text-green-300 space-y-1">
                      <p>✅ <strong>Total Amount</strong> is automatically disabled when <strong>Invoice Summary</strong> is turned OFF</p>
                      <p>✅ You cannot enable <strong>Total Amount</strong> unless <strong>Invoice Summary</strong> is enabled first</p>
                      <p>✅ This ensures logical consistency - the total amount can only show inside the summary box</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* ⭐⭐⭐ BORDERS TAB - NEW ⭐⭐⭐ */}
          {activeTab === 'borders' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Square className="text-green-500" />
                  Border Customization
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Customize borders for key invoice sections (Bill To, Vehicle Info, Invoice Details, Pickup, Return)
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <BorderCard
                  label="Bill To Section"
                  section="billTo"
                  settings={settings.borderSettings.billTo}
                  onChange={handleBorderChange}
                  icon={<User className="text-blue-400" size={20} />}
                  description="Customer information card border"
                />
                
                <BorderCard
                  label="Vehicle Information"
                  section="vehicleInfo"
                  settings={settings.borderSettings.vehicleInfo}
                  onChange={handleBorderChange}
                  icon={<Square className="text-orange-400" size={20} />}
                  description="Vehicle details card border"
                />
                
                <BorderCard
                  label="Invoice Details"
                  section="invoiceDetails"
                  settings={settings.borderSettings.invoiceDetails}
                  onChange={handleBorderChange}
                  icon={<FileText className="text-slate-400" size={20} />}
                  description="Invoice number and date card border"
                />
                
                <BorderCard
                  label="Pickup Section"
                  section="pickup"
                  settings={settings.borderSettings.pickup}
                  onChange={handleBorderChange}
                  icon={<MapPin className="text-green-400" size={20} />}
                  description="Pickup date and location card border"
                />
                
                <BorderCard
                  label="Return Section"
                  section="return"
                  settings={settings.borderSettings.return}
                  onChange={handleBorderChange}
                  icon={<MapPin className="text-red-400" size={20} />}
                  description="Return date and location card border"
                />
              </div>
              
              {/* INFO BOX */}
              <div className="mt-6 p-4 bg-blue-900/30 border border-blue-600/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-200 mb-2">Border Customization</h4>
                    <div className="text-sm text-blue-300 space-y-1">
                      <p>• Customize color, width (any CSS unit like px, rem, em), and style for each section</p>
                      <p>• Changes apply to both screen view and print output</p>
                      <p>• Preview shows how the border will appear on your invoices</p>
                      <p>• Save settings to apply changes across all invoices</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* COMPANY INFO TAB */}
          {activeTab === 'company' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Building className="text-green-500" />
                  Company Information
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Your company details that appear on invoices
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="Company Name"
                  value={settings.companyInfo.companyName}
                  onChange={(value) => handleChange('companyInfo', 'companyName', value)}
                  icon={<Building size={16} />}
                />
                
                <InputField
                  label="Email"
                  type="email"
                  value={settings.companyInfo.companyEmail}
                  onChange={(value) => handleChange('companyInfo', 'companyEmail', value)}
                  icon={<Mail size={16} />}
                />
                
                <InputField
                  label="Phone"
                  value={settings.companyInfo.companyPhone}
                  onChange={(value) => handleChange('companyInfo', 'companyPhone', value)}
                  icon={<Phone size={16} />}
                />
                
                <InputField
                  label="Website"
                  value={settings.companyInfo.companyWebsite}
                  onChange={(value) => handleChange('companyInfo', 'companyWebsite', value)}
                  icon={<Globe size={16} />}
                />
              </div>
              
              <TextareaField
                label="Address"
                value={settings.companyInfo.address}
                onChange={(value) => handleChange('companyInfo', 'address', value)}
                icon={<MapPin size={16} />}
                rows={3}
              />
            </div>
          )}
          
          {/* TEMPLATE SETTINGS TAB */}
          {activeTab === 'template' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Palette className="text-green-500" />
                  Template Settings
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Customize invoice appearance and formatting
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="Invoice Prefix"
                  value={settings.templateSettings.invoicePrefix}
                  onChange={(value) => handleChange('templateSettings', 'invoicePrefix', value)}
                  icon={<FileText size={16} />}
                />
                
                <SelectField
                  label="Date Format"
                  value={settings.templateSettings.dateFormat}
                  onChange={(value) => handleChange('templateSettings', 'dateFormat', value)}
                  options={[
                    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2025)' },
                    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2025)' },
                    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-12-31)' }
                  ]}
                  icon={<Calendar size={16} />}
                />
                
                <InputField
                  label="Currency Symbol"
                  value={settings.templateSettings.currencySymbol}
                  onChange={(value) => handleChange('templateSettings', 'currencySymbol', value)}
                  icon={<DollarSign size={16} />}
                />
                
                <SelectField
                  label="Currency Position"
                  value={settings.templateSettings.currencyPosition}
                  onChange={(value) => handleChange('templateSettings', 'currencyPosition', value)}
                  options={[
                    { value: 'before', label: 'Before Amount ($100)' },
                    { value: 'after', label: 'After Amount (100$)' }
                  ]}
                  icon={<DollarSign size={16} />}
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Primary Color</label>
                  <input
                    type="color"
                    value={settings.templateSettings.primaryColor}
                    onChange={(e) => handleChange('templateSettings', 'primaryColor', e.target.value)}
                    className="w-full h-10 rounded-lg border border-gray-600 bg-gray-700 cursor-pointer"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Accent Color</label>
                  <input
                    type="color"
                    value={settings.templateSettings.accentColor}
                    onChange={(e) => handleChange('templateSettings', 'accentColor', e.target.value)}
                    className="w-full h-10 rounded-lg border border-gray-600 bg-gray-700 cursor-pointer"
                  />
                </div>
                
                <SelectField
                  label="Font Size"
                  value={settings.templateSettings.fontSize}
                  onChange={(value) => handleChange('templateSettings', 'fontSize', value)}
                  options={[
                    { value: 'small', label: 'Small' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'large', label: 'Large' }
                  ]}
                  icon={<Type size={16} />}
                />
                
                <SelectField
                  label="Font Family"
                  value={settings.templateSettings.fontFamily}
                  onChange={(value) => handleChange('templateSettings', 'fontFamily', value)}
                  options={[
                    { value: 'Arial', label: 'Arial' },
                    { value: 'Helvetica', label: 'Helvetica' },
                    { value: 'Times New Roman', label: 'Times New Roman' },
                    { value: 'Courier', label: 'Courier' }
                  ]}
                  icon={<Type size={16} />}
                />
              </div>
            </div>
          )}
          
          {/* DOCUMENT SETTINGS TAB */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="text-green-500" />
                  Document Settings
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Configure document handling and requirements
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ToggleCard
                  label="Auto-attach Documents"
                  checked={settings.documentSettings.autoAttachDocuments}
                  onChange={(checked) => handleChange('documentSettings', 'autoAttachDocuments', checked)}
                  description="Automatically attach customer documents to invoices"
                />
                <ToggleCard
                  label="Show Document Status"
                  checked={settings.documentSettings.showDocumentStatus}
                  onChange={(checked) => handleChange('documentSettings', 'showDocumentStatus', checked)}
                  description="Display document validation status"
                />
                <ToggleCard
                  label="Allow Document Download"
                  checked={settings.documentSettings.allowDocumentDownload}
                  onChange={(checked) => handleChange('documentSettings', 'allowDocumentDownload', checked)}
                  description="Enable document download for customers"
                />
                <ToggleCard
                  label="Compress Documents"
                  checked={settings.documentSettings.compressDocuments}
                  onChange={(checked) => handleChange('documentSettings', 'compressDocuments', checked)}
                  description="Compress documents to reduce file size"
                />
              </div>
              
              <InputField
                label="Document Retention Days"
                type="number"
                value={settings.documentSettings.documentRetentionDays}
                onChange={(value) => handleChange('documentSettings', 'documentRetentionDays', parseInt(value))}
                icon={<Calendar size={16} />}
              />
            </div>
          )}
          
          {/* DISCOUNT SETTINGS TAB */}
          {activeTab === 'discount' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="text-green-500" />
                  Discount Settings
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Customize how discounts are displayed
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ToggleCard
                  label="Show Discount Badge"
                  checked={settings.discountSettings.showDiscountBadge}
                  onChange={(checked) => handleChange('discountSettings', 'showDiscountBadge', checked)}
                  description="Display discount badge on invoices with discounts"
                />
                <ToggleCard
                  label="Show Savings Amount"
                  checked={settings.discountSettings.showSavingsAmount}
                  onChange={(checked) => handleChange('discountSettings', 'showSavingsAmount', checked)}
                  description="Show how much the customer saved"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Discount Badge Color</label>
                  <input
                    type="color"
                    value={settings.discountSettings.discountBadgeColor}
                    onChange={(e) => handleChange('discountSettings', 'discountBadgeColor', e.target.value)}
                    className="w-full h-10 rounded-lg border border-gray-600 bg-gray-700 cursor-pointer"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Savings Text Color</label>
                  <input
                    type="color"
                    value={settings.discountSettings.savingsTextColor}
                    onChange={(e) => handleChange('discountSettings', 'savingsTextColor', e.target.value)}
                    className="w-full h-10 rounded-lg border border-gray-600 bg-gray-700 cursor-pointer"
                  />
                </div>
              </div>
              
              <InputField
                label="Discount Label Text"
                value={settings.discountSettings.discountLabelText}
                onChange={(value) => handleChange('discountSettings', 'discountLabelText', value)}
                icon={<DollarSign size={16} />}
              />
            </div>
          )}
          
          {/* EMAIL SETTINGS TAB */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Mail className="text-green-500" />
                  Email Configuration
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Configure email settings for automatic invoice delivery. Each company uses their own email credentials.
                </p>
              </div>

              {/* Auto Send Email Toggle */}
              <div className="flex items-center justify-between p-4 bg-blue-900/30 rounded-lg border-2 border-blue-600/50">
                <div className="flex-1">
                  <label className="font-medium text-white flex items-center gap-2 text-lg">
                    <Mail size={20} className="text-blue-400" />
                    Enable Auto Email
                  </label>
                  <p className="text-sm text-gray-300 mt-1">
                    Automatically send invoice email to customers when invoice is created
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.emailSettings.autoSendEmail || false}
                    onChange={(e) => handleChange('emailSettings', 'autoSendEmail', e.target.checked)}
                  />
                  <div className="w-14 h-7 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <SelectField
                label="Email Provider"
                value={settings.emailSettings.emailProvider || 'gmail'}
                onChange={(value) => handleChange('emailSettings', 'emailProvider', value)}
                options={[
                  { value: 'gmail', label: 'Gmail' },
                  { value: 'outlook', label: 'Outlook / Hotmail' },
                  { value: 'yahoo', label: 'Yahoo' },
                  { value: 'custom', label: 'Custom SMTP' }
                ]}
                icon={<Mail size={16} />}
              />

              <InputField
                label="Sender Email Address *"
                type="email"
                value={settings.emailSettings.senderEmail || ''}
                onChange={(value) => handleChange('emailSettings', 'senderEmail', value)}
                placeholder="your-company@gmail.com"
                icon={<Mail size={16} />}
              />

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  App Password *
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <Lock size={16} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={settings.emailSettings.senderPassword || ''}
                    onChange={(e) => handleChange('emailSettings', 'senderPassword', e.target.value)}
                    placeholder="••••••••••••••••"
                    className="w-full pl-10 pr-12 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <div className="mt-2 p-3 bg-yellow-900/30 border border-yellow-600/50 rounded-lg">
                  <p className="text-xs text-yellow-200 font-medium mb-1 flex items-center gap-2">
                    <AlertCircle size={14} />
                    Important: Use App Password, not your regular email password
                  </p>
                  <p className="text-xs text-yellow-300/80">
                    For Gmail: Google Account → Security → 2-Step Verification → App Passwords
                  </p>
                  <a 
                    href="https://support.google.com/accounts/answer/185833" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-400 hover:underline mt-1 inline-block"
                  >
                    How to generate Gmail App Password →
                  </a>
                </div>
              </div>

              {settings.emailSettings.emailProvider === 'custom' && (
                <>
                  <InputField
                    label="SMTP Host"
                    value={settings.emailSettings.smtpHost || ''}
                    onChange={(value) => handleChange('emailSettings', 'smtpHost', value)}
                    placeholder="smtp.yourprovider.com"
                    icon={<Globe size={16} />}
                  />

                  <InputField
                    label="SMTP Port"
                    type="number"
                    value={settings.emailSettings.smtpPort || 587}
                    onChange={(value) => handleChange('emailSettings', 'smtpPort', parseInt(value))}
                    placeholder="587"
                    icon={<Settings size={16} />}
                  />

                  <ToggleCard
                    label="Use SSL/TLS"
                    checked={settings.emailSettings.smtpSecure || false}
                    onChange={(checked) => handleChange('emailSettings', 'smtpSecure', checked)}
                    description="Enable secure connection (recommended)"
                  />
                </>
              )}

              <div>
                <InputField
                  label="Email Subject Template"
                  value={settings.emailSettings.subjectTemplate || 'Invoice {invoiceNumber} from {companyName}'}
                  onChange={(value) => handleChange('emailSettings', 'subjectTemplate', value)}
                  placeholder="Invoice {invoiceNumber} from {companyName}"
                  icon={<Mail size={16} />}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Available variables: {'{invoiceNumber}'}, {'{customerName}'}, {'{companyName}'}
                </p>
              </div>

              <InputField
                label="Invoice View URL (Optional)"
                type="url"
                value={settings.emailSettings.invoiceViewUrl || ''}
                onChange={(value) => handleChange('emailSettings', 'invoiceViewUrl', value)}
                placeholder="https://your-app.com"
                icon={<Globe size={16} />}
              />

              <div>
                <InputField
                  label="CC Email Addresses (Optional)"
                  value={settings.emailSettings.ccAddresses?.join(', ') || ''}
                  onChange={(value) => handleChange('emailSettings', 'ccAddresses', value.split(',').map(email => email.trim()).filter(Boolean))}
                  placeholder="admin@company.com, manager@company.com"
                  icon={<Mail size={16} />}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Separate multiple emails with commas
                </p>
              </div>

              <div>
                <InputField
                  label="BCC Email Addresses (Optional)"
                  value={settings.emailSettings.bccAddresses?.join(', ') || ''}
                  onChange={(value) => handleChange('emailSettings', 'bccAddresses', value.split(',').map(email => email.trim()).filter(Boolean))}
                  placeholder="records@company.com"
                  icon={<Mail size={16} />}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Blind carbon copy - recipients won't see these addresses
                </p>
              </div>

              <div className="mt-8 p-6 bg-gray-800/50 rounded-lg border border-gray-700">
                <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                  <Info size={18} className="text-blue-400" />
                  Test Email Configuration
                </h4>
                <p className="text-sm text-gray-300 mb-4">
                  After saving your settings, create a test invoice to verify email delivery is working correctly.
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle size={16} className="text-green-500" />
                    <span>Configuration will be validated when you save</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 bg-red-900/20 border border-red-600/50 rounded-lg">
                <div className="flex gap-3">
                  <Shield size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-200 mb-1">Security Notice</h4>
                    <p className="text-sm text-red-300/90">
                      Your email credentials are stored securely and encrypted in Firebase. They are only accessible by your company's 
                      Cloud Functions and are never exposed to the client application or other companies.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* PRINT SETTINGS TAB */}
          {activeTab === 'print' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Printer className="text-green-500" />
                  Print Settings
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Configure print and export options
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ToggleCard
                  label="Show Print Button"
                  checked={settings.printSettings?.showPrintButton || false}
                  onChange={(checked) => handleChange('printSettings', 'showPrintButton', checked)}
                  description="Display print button on invoices"
                />
                <ToggleCard
                  label="Show Download Button"
                  checked={settings.printSettings?.showDownloadButton || false}
                  onChange={(checked) => handleChange('printSettings', 'showDownloadButton', checked)}
                  description="Display PDF download button on invoices"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <SelectField
                  label="Paper Size"
                  value={settings.printSettings?.paperSize || 'A4'}
                  onChange={(value) => handleChange('printSettings', 'paperSize', value)}
                  options={[
                    { value: 'A4', label: 'A4' },
                    { value: 'Letter', label: 'Letter' },
                    { value: 'Legal', label: 'Legal' }
                  ]}
                  icon={<Printer size={16} />}
                />
                
                <SelectField
                  label="Orientation"
                  value={settings.printSettings?.orientation || 'portrait'}
                  onChange={(value) => handleChange('printSettings', 'orientation', value)}
                  options={[
                    { value: 'portrait', label: 'Portrait' },
                    { value: 'landscape', label: 'Landscape' }
                  ]}
                  icon={<Layout size={16} />}
                />
                
                <SelectField
                  label="Margins"
                  value={settings.printSettings?.margins || 'normal'}
                  onChange={(value) => handleChange('printSettings', 'margins', value)}
                  options={[
                    { value: 'none', label: 'None' },
                    { value: 'narrow', label: 'Narrow' },
                    { value: 'normal', label: 'Normal' },
                    { value: 'wide', label: 'Wide' }
                  ]}
                  icon={<Layout size={16} />}
                />
              </div>
            </div>
          )}
          
          {/* TERMS & CONDITIONS TAB */}
          {activeTab === 'terms' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="text-green-500" />
                  Terms & Conditions
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Set default terms and conditions for invoices. This is the single source of truth for terms display.
                </p>
              </div>
              
              <ToggleCard
                label="Show Terms & Conditions Section"
                checked={settings.termsSettings?.showTerms || false}
                onChange={(checked) => handleChange('termsSettings', 'showTerms', checked)}
                description="Display the entire terms and conditions section on invoices"
              />
              
              <ToggleCard
                label="Show Terms & Conditions Heading"
                checked={settings.termsSettings?.showTermsHeading !== false}
                onChange={(checked) => handleChange('termsSettings', 'showTermsHeading', checked)}
                description="Display the heading/title above the terms and conditions text"
              />
              
              <InputField
                label="Section Heading"
                value={settings.termsSettings?.termsHeading || ''}
                onChange={(value) => handleChange('termsSettings', 'termsHeading', value)}
                placeholder="Terms & Conditions"
              />
              
              <TextareaField
                label="Terms & Conditions Text"
                value={settings.termsSettings?.termsText || ''}
                onChange={(value) => handleChange('termsSettings', 'termsText', value)}
                rows={6}
                placeholder="Enter your terms and conditions..."
              />
            </div>
          )}
          
          {/* FOOTER SETTINGS TAB */}
          {activeTab === 'footer' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Layout className="text-green-500" />
                  Footer Settings
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Customize invoice footer information
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ToggleCard
                  label="Show Footer"
                  checked={settings.footerSettings?.showFooter || false}
                  onChange={(checked) => handleChange('footerSettings', 'showFooter', checked)}
                  description="Display invoice footer"
                />
                <ToggleCard
                  label="Show Page Numbers"
                  checked={settings.footerSettings?.showPageNumbers || false}
                  onChange={(checked) => handleChange('footerSettings', 'showPageNumbers', checked)}
                  description="Display page numbers in footer"
                />
                <ToggleCard
                  label="Show Generated By"
                  checked={settings.footerSettings?.showGeneratedBy || false}
                  onChange={(checked) => handleChange('footerSettings', 'showGeneratedBy', checked)}
                  description="Show who generated the invoice"
                />
              </div>
              
              <TextareaField
                label="Footer Text"
                value={settings.footerSettings?.footerText || ''}
                onChange={(value) => handleChange('footerSettings', 'footerText', value)}
                icon={<FileText size={16} />}
                rows={3}
                placeholder="Thank you for your business!"
              />
            </div>
          )}
          
          {/* ADVANCED SETTINGS TAB */}
          {activeTab === 'advanced' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Settings className="text-green-500" />
                  Advanced Settings
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Advanced configuration options
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ToggleCard
                  label="Enable Audit Log"
                  checked={settings.advancedSettings?.enableAuditLog || false}
                  onChange={(checked) => handleChange('advancedSettings', 'enableAuditLog', checked)}
                  description="Track all invoice changes"
                />
                <ToggleCard
                  label="Auto Numbering"
                  checked={settings.advancedSettings?.autoNumbering || false}
                  onChange={(checked) => handleChange('advancedSettings', 'autoNumbering', checked)}
                  description="Automatically generate invoice numbers"
                />
                <ToggleCard
                  label="Enable Notifications"
                  checked={settings.advancedSettings?.enableNotifications || false}
                  onChange={(checked) => handleChange('advancedSettings', 'enableNotifications', checked)}
                  description="Send notifications for invoice events"
                />
                <ToggleCard
                  label="Debug Mode"
                  checked={settings.advancedSettings?.debugMode || false}
                  onChange={(checked) => handleChange('advancedSettings', 'debugMode', checked)}
                  description="Enable detailed logging"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  label="Invoice Number Format"
                  value={settings.advancedSettings?.invoiceNumberFormat || 'INV-YYYY-NNNN'}
                  onChange={(value) => handleChange('advancedSettings', 'invoiceNumberFormat', value)}
                  icon={<FileText size={16} />}
                  placeholder="INV-YYYY-NNNN"
                />
                
                <SelectField
                  label="Timezone"
                  value={settings.advancedSettings?.timezone || 'UTC'}
                  onChange={(value) => handleChange('advancedSettings', 'timezone', value)}
                  options={[
                    { value: 'UTC', label: 'UTC' },
                    { value: 'America/New_York', label: 'Eastern Time' },
                    { value: 'America/Los_Angeles', label: 'Pacific Time' },
                    { value: 'Europe/London', label: 'London' },
                    { value: 'Asia/Tokyo', label: 'Tokyo' }
                  ]}
                  icon={<Globe size={16} />}
                />
                
                <SelectField
                  label="Language"
                  value={settings.advancedSettings?.language || 'en'}
                  onChange={(value) => handleChange('advancedSettings', 'language', value)}
                  options={[
                    { value: 'en', label: 'English' },
                    { value: 'es', label: 'Spanish' },
                    { value: 'fr', label: 'French' },
                    { value: 'de', label: 'German' }
                  ]}
                  icon={<Globe size={16} />}
                />
              </div>
            </div>
          )}
          
        </div>
        
        {/* SAVE BUTTON */}
        <div className="mt-6 flex items-center justify-between">
          <div>
            {saveStatus === 'success' && (
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle size={20} />
                <span className="font-medium">Settings saved successfully! Hard refresh (Ctrl+Shift+R) to see changes.</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className="flex items-center gap-2 text-red-400">
                <AlertCircle size={20} />
                <span className="font-medium">Failed to save settings</span>
              </div>
            )}
          </div>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
        
      </div>
    </div>
  );
};

export default InvoiceSettingsControlPanel;